import { useState, useEffect } from "react";
import { Button, Modal, message } from "antd";
import { useSelector } from "react-redux";
import "./EmptyDashboardMenu.css";
import templateData from "../Templates/dashboardTemplates.json";
import { useDashboard } from "../Contexts/dashboardHooks";
import { applyTemplate, detectTemplateVersion } from "../Templates/templateProcessor";
import { resolveTemplateV2 } from "../Templates/templateResolver";
import { useTemplateResolveContext } from "../Templates/templateContext";
import type { TemplateFileV2 } from "../Templates/templateTokens";

import template1 from "../Templates/Template 1-full.json";
import template2 from "../Templates/Template 2-full.json";
import template3 from "../Templates/Template 3-full.json";

import templateImg1 from "./img/template_1.png";
import templateImg2 from "./img/template_2.png";
import templateImg3 from "./img/template_3.png";

// v2 packages: drop a folder Templates/<workflow>/{template.json,preview.png} and add
// one catalog entry — no imports to edit. Discovered at build time by Vite.
const v2Files = import.meta.glob("../Templates/*/template.json", { eager: true }) as Record<string, { default: Record<string, any> }>;
const v2Images = import.meta.glob("../Templates/*/preview.png", { eager: true, query: "?url", import: "default" }) as Record<string, string>;
const folderOf = (path: string): string => /\/Templates\/([^/]+)\//.exec(path)?.[1] ?? path;

const TEMPLATE_FILES: Record<string, Record<string, any>> = {
  "Template 1-full": template1,
  "Template 2-full": template2,
  "Template 3-full": template3,
  ...Object.fromEntries(Object.entries(v2Files).map(([p, m]) => [folderOf(p), m.default ?? m])),
};

const TEMPLATE_IMAGES: Record<string, string> = {
  "Template 1-full": templateImg1,
  "Template 2-full": templateImg2,
  "Template 3-full": templateImg3,
  ...Object.fromEntries(Object.entries(v2Images).map(([p, url]) => [folderOf(p), url])),
};

interface Template {
  id: string;
  title: string;
  description: string;
  imageUrl: string | null;
  workflow: string;
  templateFile: string;
}

interface Props {
  onAddWidget: () => void;
}

export function EmptyDashboardMenu({ onAddWidget }: Props) {
  const [visible, setVisible] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  const { importDashboard, renameDashboard } = useDashboard();
  const allMetricTypes: Record<string, any[]> = useSelector(({ team }: any) => team.all_metric_types);
  const buildResolveCtx = useTemplateResolveContext();

  useEffect(() => {
    const id = setTimeout(() => setVisible(true), 40);
    return () => clearTimeout(id);
  }, []);

  const closePreview = () => setPreviewTemplate(null);

  const handleUseTemplate = () => {
    if (!previewTemplate) return;

    const rawTemplate = TEMPLATE_FILES[previewTemplate.templateFile];
    if (!rawTemplate) {
      closePreview();
      onAddWidget();
      return;
    }

    let payload;
    let substitutions: string[] = [];
    if (detectTemplateVersion(rawTemplate) === 2) {
      const { payload: p, report } = resolveTemplateV2(
        rawTemplate as unknown as TemplateFileV2,
        buildResolveCtx(previewTemplate.title),
      );
      payload = p;
      substitutions = report.substitutions;
    } else {
      const gpsMetrics = (allMetricTypes?.gps ?? []).map((m: any) => ({
        id: m.unique_identifier as string,
        name: m.name as string,
      }));
      payload = applyTemplate(rawTemplate, {
        gpsMetrics,
        dashboardName: previewTemplate.title,
      });
    }

    closePreview();
    importDashboard(payload);
    renameDashboard(previewTemplate.title);
    if (substitutions.length) {
      message.info(
        substitutions.length === 1
          ? substitutions[0]
          : `${substitutions.length} metrics/subjects were adapted for your team.`,
      );
    }
  };

  return (
    <>
      <div className={`empty-dashboard-menu${visible ? " edm-visible" : ""}`}>
        <div className="edm-layout">

          <div className="edm-panel">
            <div className="edm-panel-header">
              <h1 className="edm-title">Start with a ready-made dashboard</h1>
              <p className="edm-subtitle">
                Each layout is pre-configured for GPS and training data. Pick one to get your analysis on screen immediately, or start from scratch.
              </p>
            </div>

            <div className="edm-cards">
              {(templateData.templates as Template[]).map((tpl) => (
                <button
                  key={tpl.id}
                  className="edm-card"
                  type="button"
                  onClick={() => setPreviewTemplate(tpl)}
                >
                  <div className="edm-card-preview">
                    <img
                      src={TEMPLATE_IMAGES[tpl.templateFile] ?? tpl.imageUrl ?? undefined}
                      alt={tpl.title}
                      className="edm-card-img"
                    />
                    <div className="edm-card-overlay">
                      <span className="edm-card-overlay-label">Preview</span>
                    </div>
                  </div>
                  <div className="edm-card-body">
                    <p className="edm-card-title">{tpl.title}</p>
                    <p className="edm-card-desc">{tpl.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <button className="edm-hint" type="button" onClick={onAddWidget}>
            <svg
              viewBox="0 0 90 208"
              fill="none"
              className="edm-hint-svg"
              aria-hidden="true"
            >
              {/*
                Shaft bows left then exits upper-right.
                pathLength="1" means stroke-dashoffset 1→0 draws the full path.
                End (66,11), last control (8,62) → tangent ≈ (0.74, -0.67).
              */}
              <path
                className="edm-arrow-shaft"
                pathLength="1"
                d="M 45 198 C 8 155, 8 62, 66 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              {/*
                Filled arrowhead aligned to exit tangent.
                Tip (66,11); base 13px back → (56,19); half-width ±6px.
              */}
              <path
                className="edm-arrow-head"
                d="M 66 11 L 60 24 L 52 16 Z"
                fill="currentColor"
              />
            </svg>
            <span className="edm-hint-text">
              Or click here to build<br />from scratch
            </span>
          </button>

        </div>
      </div>

      <Modal
        open={previewTemplate !== null}
        onCancel={closePreview}
        title={previewTemplate?.title}
        width={860}
        centered
        destroyOnHidden
        footer={
          <div className="edm-modal-footer">
            <Button type="primary" onClick={handleUseTemplate}>
              Use Template
            </Button>
            <Button onClick={closePreview}>Cancel</Button>
          </div>
        }
      >
        {previewTemplate && (
          <>
            <img
              src={TEMPLATE_IMAGES[previewTemplate.templateFile] ?? previewTemplate.imageUrl ?? undefined}
              alt={previewTemplate.title}
              className="edm-modal-img"
            />
            <p className="edm-modal-desc">{previewTemplate.description}</p>
          </>
        )}
      </Modal>
    </>
  );
}
